import { signOut } from 'firebase/auth';
import { createContext, useContext, useEffect, useState } from 'react';
import { clearCredentials, loadCredentials, useApiClient } from '../../apiClient';
import { auth } from './firebase';
import { useSignIn } from './hooks/useSignIn';
import { decodeJWT } from './utils';

/**
 * End the FIREBASE session too, not just our two localStorage keys.
 *
 * `useSignIn` calls `signInWithEmailAndPassword(auth, …)`, and `firebase.ts`
 * takes the SDK default persistence (`browserLocalPersistence` — IndexedDB,
 * survives a browser restart). Clearing `idToken`/`refreshToken` leaves that
 * session intact, so on a shared front-desk machine the next person could call
 * `auth.currentUser.getIdToken()` and mint a working API token for the
 * operator who thought they had logged out.
 *
 * Fire-and-forget on purpose: `signOut` does a network round-trip and the
 * local sign-out must not depend on it. Local state is cleared by the caller
 * first, so a rejection here cannot leave the user looking signed in.
 */
function endFirebaseSession() {
    signOut(auth).catch(() => {
        // Already signed out, or offline. The credentials are gone either way.
    });
}

const AuthContext = createContext<{
    login: (email: string, password: string) => Promise<void>;
    logout: () => void;
    setCurrentUser: (u: Record<string, unknown>) => void;
    currentUser: Record<string, unknown> | null; loading?: boolean; signingIn?: boolean
}>({ currentUser: null, setCurrentUser: () => { }, logout: () => { }, login: async () => { } });

export const AuthProvider = ({ children }: { children?: React.ReactNode }) => {
    const [currentUser, setCurrentUser] = useState<Record<string, unknown> | null>(null);
    const { isLoading: signingIn, mutateAsync: signIn } = useSignIn((data) => {
        const decodedUser = decodeJWT(data.accessToken);
        setCurrentUser(decodedUser);
    });
    const [isInitializing, setIsInitializing] = useState(true);
    const apiClient = useApiClient();

    useEffect(() => {
        try {
            const credentials = loadCredentials();
            if (credentials && credentials.idToken) {
                const decodedUser = decodeJWT(credentials.idToken);
                setCurrentUser(decodedUser);
            }
        } catch (error) {
            setIsInitializing(false);
        }
        setIsInitializing(false);
    }, []);

    useEffect(() => {
        // Every token refresh re-decodes the claims into currentUser, so a
        // role change (admin ↔ coach) takes effect at the next refresh
        // instead of holding stale navigation until a manual reload. The SPA
        // can stay open for days — currentUser was previously only derived at
        // mount and sign-in.
        apiClient.setTokenRefreshedCallback((idToken: string) => {
            try {
                setCurrentUser(decodeJWT(idToken));
            } catch {
                // A token we can't decode changes nothing — keep the old user
                // and let the API reject the token if it's actually invalid.
            }
        });

        // Force-logout. apiClient calls this when a request still comes back
        // unauthorized AFTER a token refresh — i.e. the refresh token is dead
        // or the account was revoked/disabled. Until this was wired the
        // callback was a no-op, so a revoked admin kept a signed-in-looking
        // SPA (every request failing) until they reloaded by hand. Clearing
        // credentials + currentUser drops them back to the login screen.
        apiClient.setSignOutCallback(() => {
            clearCredentials();
            setCurrentUser(null);
            endFirebaseSession();
        });
    }, [apiClient]);

    const login = async (email: string, password: string) => {
        await signIn({ email, password });
    };

    const logout = () => {
        clearCredentials();
        setCurrentUser(null);
        endFirebaseSession();
    };

    return (
        <AuthContext.Provider value={{ currentUser, setCurrentUser, loading: false, signingIn, login, logout }}>
            {isInitializing ? null : children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);